// src/features/dashboard/MealCard.tsx
import React from "react";
import {
  Card,
  Typography,
  Box,
  Chip,
  Badge,
} from "@mui/material";
import { CheckCircle, Restaurant } from "@mui/icons-material";
import dayjs from "dayjs";

interface Recipe {
  recipe_id: number;
  name: string;
  image_url?: string | null;
}

interface MealCardProps {
  mealType: string;
  recipe: Recipe;
  onClick?: () => void;
  status?: string;
  completedAt?: string;
  isLeftover?: boolean;
  extraServings?: number;
  servings?: number;
}

const mealTypeLabels: Record<string, string> = {
  breakfast: "Breakfast",
  lunch: "Lunch",
  dinner: "Dinner",
};

const getMealTypeColor = (mealType: string) => {
  switch (mealType.toLowerCase()) {
    case "breakfast": return "primary";
    case "lunch": return "secondary";
    case "dinner": return "error";
    default: return "default";
  }
};

const MealCard: React.FC<MealCardProps> = ({
  mealType,
  recipe,
  onClick,
  status,
  completedAt,
  isLeftover,
  extraServings,
  servings,
}) => {
  const isCompleted = status === "completed";

  return (
    <Badge
      badgeContent={
        isCompleted ? (
          <CheckCircle
            sx={{
              color: "success.main",
              fontSize: 24,
              bgcolor: "white",
              borderRadius: "50%",
            }}
          />
        ) : null
      }
      sx={{
        "& .MuiBadge-badge": {
          right: 12,
          top: 12,
        },
        width: "100%",
      }}
    >
      <Card
        elevation={2}
        sx={{
          width: "100%",
          cursor: "pointer",
          overflow: "hidden",
          border: isCompleted
            ? "2px solid"
            : isLeftover
            ? "2px dashed"
            : "1px solid",
          borderColor: isCompleted
            ? "success.main"
            : isLeftover
            ? "info.light"
            : "divider",
          borderRadius: 2,
          "&:hover": {
            boxShadow: 4,
            transform: "translateY(-2px)",
          },
          transition: "box-shadow 0.2s, transform 0.2s",
        }}
        onClick={onClick}
      >
        {/* Image container with overlays */}
        <Box sx={{ position: "relative", height: 160, overflow: "hidden" }}>
          {/* Background image or placeholder */}
          {recipe.image_url ? (
            <Box
              component="img"
              src={recipe.image_url}
              alt={recipe.name}
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = "none";
              }}
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          ) : (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                bgcolor: "grey.200",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Restaurant sx={{ fontSize: 48, color: "grey.400" }} />
            </Box>
          )}

          {/* Top row: meal type badge + leftover badge */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              p: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Box display="flex" gap={0.5}>
              {isLeftover ? (
                <Chip
                  label="Leftover"
                  size="small"
                  sx={{
                    bgcolor: "rgba(227, 242, 253, 0.9)",
                    color: "#1565c0",
                    fontWeight: 600,
                    fontSize: "0.7rem",
                  }}
                />
              ) : (
                <Chip
                  label={mealTypeLabels[mealType.toLowerCase()] || mealType}
                  color={getMealTypeColor(mealType) as any}
                  size="small"
                  sx={{ fontWeight: 600, fontSize: "0.7rem" }}
                />
              )}
            </Box>
          </Box>

          {/* Bottom overlay with gradient + text */}
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              background: "linear-gradient(transparent, rgba(0,0,0,0.8))",
              p: 1.5,
              pt: 3,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "white",
                fontWeight: 700,
                lineHeight: 1.2,
                textShadow: "0 1px 3px rgba(0,0,0,0.5)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {recipe.name}
            </Typography>
            <Box display="flex" gap={1} alignItems="center" mt={0.5}>
              {isLeftover ? (
                <Typography
                  variant="caption"
                  sx={{ color: "rgba(255,255,255,0.8)", fontSize: "0.7rem" }}
                >
                  Reheat {servings} serving{servings !== 1 ? "s" : ""}
                </Typography>
              ) : extraServings && extraServings > 0 ? (
                <Typography
                  variant="caption"
                  sx={{ color: "rgba(255,255,255,0.8)", fontSize: "0.7rem" }}
                >
                  {(servings || 0) + extraServings} servings (+{extraServings}{" "}
                  extra)
                </Typography>
              ) : null}
              {isCompleted && completedAt && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "#81c784",
                    fontSize: "0.7rem",
                    fontWeight: 500,
                  }}
                >
                  Done {dayjs(completedAt).format("MMM D")}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Card>
    </Badge>
  );
};

export default MealCard;
